import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMessage } from '../contexts/MessageContext'; // Importar el hook de mensajes
import { ordenarPreguntasPorDificultad, obtenerRespuestasMezcladas } from '../utils/quizLogic';
import { tensionSoundFadeIn, tensionSoundFadeOut, reproducirSonidoAcierto, reproducirSonidoError, inicializarSonidosYReferencias } from '../utils/quizSounds';
import { API_BASE_URL } from '../config/apiConfig';

// Hook personalizado para la lógica del quiz
const useQuiz = () => {
  const navigate = useNavigate();
  const { showMessage: setMessage } = useMessage(); // Obtener la función de mensajes del contexto
  const [preguntas, setPreguntas] = useState([]);
  const [preguntaActual, setPreguntaActual] = useState(0);
  const [respuestaSeleccionada, setRespuestaSeleccionada] = useState(null);
  const [respuestaRevelada, setRespuestaRevelada] = useState(false);
  const [respuestas, setRespuestas] = useState([]);
  const [puntuacion, setPuntuacion] = useState(0);
  const [juegoTerminado, setJuegoTerminado] = useState(false);
  const [respuestasMezcladas, setRespuestasMezcladas] = useState([]);
 const [respuestasEliminadas, setRespuestasEliminadas] = useState([]);
  const [respuestasVisibles, setRespuestasVisibles] = useState(0);
  const [primeraCarga, setPrimeraCarga] = useState(true);

    const [fallos, setFallos] = useState(0);

  const registrarRespuesta = (respuestaIndex, esCorrecta) => {
    if (respuestas[preguntaActual] !== null) {
      return;
    }

    const nuevasRespuestas = [...respuestas];
    nuevasRespuestas[preguntaActual] = respuestaIndex;
    setRespuestas(nuevasRespuestas);

    if (esCorrecta) {
      setPuntuacion(prevPuntuacion => prevPuntuacion + 1);
      return;
    }

    setFallos(prevFallos => prevFallos + 1);
  };

  // Estados para el temporizador
  const [duracionConcurso, setDuracionConcurso] = useState(0);
  const [tiempoRestante, setTiempoRestante] = useState(0);
  const [temporizadorActivo, setTemporizadorActivo] = useState(false);
  const [tiempoPorPregunta, setTiempoPorPregunta] = useState(0);

  // Referencias para los sonidos
 const aciertoSoundRef = useRef(null);
  const errorSoundRef = useRef(null);
  const tensionSoundRef = useRef(null);
    const preguntasPreviasRef = useRef([]);

  // Iniciar concurso - carga preguntas y reproduce música de tensión
 const iniciarConcurso = async () => {
    try {
      // Obtener las preguntas que ya fueron seleccionadas por iniciar_concurso.php y almacenadas en sessionStorage
      const preguntasAlmacenadas = sessionStorage.getItem('preguntasConcurso');
      let preguntasSeleccionadas = [];

      if (preguntasAlmacenadas) {
        preguntasSeleccionadas = JSON.parse(preguntasAlmacenadas);
      } else {
        // Si no hay preguntas almacenadas, obtenerlas desde el endpoint como fallback
        const cantidadEsperada = parseInt(sessionStorage.getItem('cantidadPreguntas') || '12');
        const response = await axios.get(`${API_BASE_URL}api/obtener_preguntas_concurso.php?cantidad=${cantidadEsperada}`);
        preguntasSeleccionadas = response.data.preguntas;
      }

      // Contar preguntas por dificultad para mostrar en consola (antes de ordenar)
      const faciles = preguntasSeleccionadas.filter(p => p.dificultad && (p.dificultad.toLowerCase().includes('facil') || p.dificultad.toLowerCase().includes('fácil'))).length;
      const medias = preguntasSeleccionadas.filter(p => p.dificultad && (p.dificultad.toLowerCase().includes('medio') || p.dificultad.toLowerCase().includes('media'))).length;
      const dificiles = preguntasSeleccionadas.filter(p => p.dificultad && (p.dificultad.toLowerCase().includes('dificil') || p.dificultad.toLowerCase().includes('difícil'))).length;

      // console.log(`Concurso iniciado - Preguntas: Fáciles: ${faciles}, Medias: ${medias}, Difíciles: ${dificiles}`);

      // Ordenar las preguntas por dificultad
      const preguntasOrdenadas = ordenarPreguntasPorDificultad(preguntasSeleccionadas);
      setPreguntas(preguntasOrdenadas);
      setRespuestas(preguntasOrdenadas.map(() => null));

      // Reproducir la música de tensión con fade in al iniciar el concurso
      // Esperar un poco para asegurar que los sonidos estén inicializados
      setTimeout(() => {
        tensionSoundFadeIn(tensionSoundRef);
      }, 10); // Pequeño retraso para asegurar la inicialización
    } catch (error) {
      console.error('Error al cargar preguntas:', error);
      if (typeof setMessage === 'function') {
        setMessage('Error al cargar las preguntas del concurso', 'error');
      } else {
        // Fallback si no hay setMessage disponible
        console.error('Error al cargar las preguntas del concurso');
      }
    }
  };

  // Cargar preguntas (mantiene la funcionalidad anterior sin reproducir música)
  const cargarPreguntas = async () => {
    try {
      // Obtener las preguntas que ya fueron seleccionadas por iniciar_concurso.php y almacenadas en sessionStorage
      const preguntasAlmacenadas = sessionStorage.getItem('preguntasConcurso');
      let preguntasSeleccionadas = [];

      if (preguntasAlmacenadas) {
        preguntasSeleccionadas = JSON.parse(preguntasAlmacenadas);
      } else {
        // Si no hay preguntas almacenadas, obtenerlas desde el endpoint como fallback
        const cantidadPreguntas = sessionStorage.getItem('cantidadPreguntas') || '12';
        const response = await axios.get(`${API_BASE_URL}api/obtener_preguntas_concurso.php?cantidad=${cantidadPreguntas}`);
        preguntasSeleccionadas = response.data.preguntas;
      }

      // Ordenar las preguntas por dificultad
      const preguntasOrdenadas = ordenarPreguntasPorDificultad(preguntasSeleccionadas);
      setPreguntas(preguntasOrdenadas);
      setRespuestas(preguntasOrdenadas.map(() => null));
    } catch (error) {
      console.error('Error al cargar preguntas:', error);
      if (typeof setMessage === 'function') {
        setMessage('Error al cargar las preguntas del concurso', 'error');
      } else {
        // Fallback si no hay setMessage disponible
        console.error('Error al cargar las preguntas del concurso');
      }
    }
  };

   // Inicializar respuestas mezcladas
  useEffect(() => {
    if (preguntas.length > 0) {
      setRespuestasMezcladas(prev => {
        return preguntas.map((pregunta, index) => {
          const mezclaExistente = prev[index];
          const preguntaPrevia = preguntasPreviasRef.current[index];

          if (mezclaExistente && preguntaPrevia?.id === pregunta.id) {
            return mezclaExistente;
          }

          return obtenerRespuestasMezcladas(pregunta);
        });
      });

      preguntasPreviasRef.current = preguntas;
    }
  }, [preguntas]);

  // Manejar selección de respuesta
  const manejarSeleccionRespuesta = (respuestaIndex) => {
    if (!respuestaRevelada && respuestasVisibles === 4) {
      setRespuestaSeleccionada(respuestaIndex);
    }
  };

   // Resolver pregunta
  const resolverPregunta = (respuestaIndex = respuestaSeleccionada) => {
    const esEventoReact = respuestaIndex && typeof respuestaIndex === 'object' && typeof respuestaIndex.preventDefault === 'function';
    const indiceRespuesta = esEventoReact ? respuestaSeleccionada : respuestaIndex;

    if (indiceRespuesta === null || typeof indiceRespuesta === 'undefined' || respuestaRevelada) return;
    
    setTemporizadorActivo(false);
    
    // Hacer fade out del sonido de tensión al resolver la pregunta
    tensionSoundFadeOut(tensionSoundRef);
    
    if (indiceRespuesta !== respuestaSeleccionada) {
      setRespuestaSeleccionada(indiceRespuesta);
    }

    setRespuestaRevelada(true);
    
    const esCorrecta = indiceRespuesta === indiceRespuestaCorrecta;

    registrarRespuesta(indiceRespuesta, esCorrecta);
    
    if (esCorrecta) {
      reproducirSonidoAcierto(aciertoSoundRef);
    } else {
      reproducirSonidoError(errorSoundRef);
    }
  };

  // Pasar a la siguiente pregunta
  const siguientePregunta = () => {
    if (preguntaActual < preguntas.length - 1) {
      setPreguntaActual(preguntaActual + 1);
      setRespuestaSeleccionada(null);
      setRespuestaRevelada(false);
      
      if (tiempoPorPregunta > 0) {
        setTiempoRestante(tiempoPorPregunta);
        setTemporizadorActivo(false);
        setRespuestasVisibles(0);
      }
      
      // Reproducir la música de tensión con fade in al pasar a la siguiente pregunta
      tensionSoundFadeIn(tensionSoundRef);
      
      // Mensaje por consola con la nueva pregunta y su dificultad
      const nuevaPregunta = preguntas[preguntaActual + 1];
      // console.log(`Nueva pregunta: "${nuevaPregunta.pregunta}" - Dificultad: ${nuevaPregunta.dificultad}`);
    // } else {
    //   setJuegoTerminado(true);
    }
 };

  // Reiniciar concurso
 const reiniciarConcurso = (onResetComodines) => {
    setPreguntaActual(0);
    setRespuestaSeleccionada(null);
    setRespuestaRevelada(false);
    setRespuestas(preguntas.map(() => null));
    setPuntuacion(0);
    setJuegoTerminado(false);
    setFallos(0); // Reiniciar fallos
    setRespuestasEliminadas(preguntas.map(() => []));
    tensionSoundFadeIn(tensionSoundRef);
    // Si se proporciona una función para reiniciar comodines, llamarla
    if (onResetComodines && typeof onResetComodines === 'function') {
      onResetComodines();
    }
 };

  // Calcular índice de la respuesta correcta
  const respuestasMezcladasPreguntaActual = respuestasMezcladas[preguntaActual] || [];
  const indiceRespuestaCorrecta = respuestasMezcladasPreguntaActual.findIndex(respuesta => respuesta.esCorrecta);

   return {
    preguntas,
    preguntaActual,
    respuestaSeleccionada,
    respuestaRevelada,
    respuestas,
    puntuacion,
    juegoTerminado,
    respuestasMezcladasPreguntaActual,
    respuestasMezcladas, // Exponer el array completo de respuestas mezcladas
    respuestasEliminadas,
    respuestasVisibles,
    duracionConcurso,
    tiempoRestante,
    temporizadorActivo,
    tiempoPorPregunta,
    primeraCarga,
    indiceRespuestaCorrecta,
    cargarPreguntas,
    iniciarConcurso,
    manejarSeleccionRespuesta,
    resolverPregunta,
    siguientePregunta,
    reiniciarConcurso,
    setPreguntaActual,
    setRespuestaSeleccionada,
    setRespuestaRevelada,
    setPreguntas,
    setRespuestas,
    setPuntuacion,
    setJuegoTerminado,
    setRespuestasMezcladas,
    setRespuestasEliminadas,
    setRespuestasVisibles,
    setDuracionConcurso,
    setTiempoRestante,
    setTemporizadorActivo,
    setTiempoPorPregunta,
    aciertoSoundRef,
    errorSoundRef,
    tensionSoundRef,
    tensionSoundFadeIn, // Exportar la función para que pueda usarse en el componente
    tensionSoundFadeOut, // Exportar la función para que pueda usarse en el componente
    reproducirSonidoAcierto,
    reproducirSonidoError,
    fallos,
    setFallos,
    inicializarSonidosYReferencias,
    navigate
  };
};

export default useQuiz;
